import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'

import type { FramesManager } from 'shared-libs/components/view/frames-manager'
import type { UI2CheckboxFieldProps } from 'shared-libs/components/ui2/fields/ui2-checkbox-field/ui2-checkbox-field-props'
import { UI2_CHECKBOX_FIELD_DEFAULT_PROPS } from 'shared-libs/components/ui2/fields/ui2-checkbox-field/ui2-checkbox-field-props'
import type {
  IFieldShellEditableExtras,
  IFieldShellReadOnlyHelpers,
} from 'shared-libs/components/ui2/fields/field-ui/field-shell/field-shell-props'

import { UI2FieldControl } from 'browser/components/ui2/fields/field-ui/field-control/field-control'
import { IUI2FieldShellProps, UI2FieldShell } from '../field-ui/field-shell/field-shell'
import { UI2Label } from '../field-ui/label/label'
import './_ui2-checkbox-field.scss'

interface IUI2CheckboxWebExtras {
  onChange?: (value: any) => void
  className?: string
  style?: React.CSSProperties
  frames?: FramesManager
  translationTable?: any
  isHorizontalLayout?: boolean
  errors?: any[]
}

/**
 * @uiComponent
 */
export interface IUI2CheckboxFieldProps extends UI2CheckboxFieldProps, IUI2CheckboxWebExtras {}

export class UI2CheckboxField extends React.Component<IUI2CheckboxFieldProps> {
  public static defaultProps: Partial<IUI2CheckboxFieldProps> = {
    ...UI2_CHECKBOX_FIELD_DEFAULT_PROPS,
  }

  public render() {
    return this.props.isHorizontalLayout !== false ? this.renderHorizontal() : this.renderTwoColumn()
  }

  private renderHorizontal() {
    const { className, value, isDisabled, accessibilityLabel, label, testID } = this.props
    const renderReadOnly = (val: boolean | undefined, { editNode }: { editNode?: React.ReactNode }) => (
      <>
        <div className='ui2-fieldShell-readOnly-content'>
          <span className='ui2-checkboxField-readOnlyText'>{val ? 'Yes' : 'No'}</span>
        </div>
        {editNode}
      </>
    )
    return (
      <UI2FieldShell
        {...(this.props as unknown as IUI2FieldShellProps)}
        className={classNames('ui2-checkboxField', className)}
        renderReadOnly={renderReadOnly}
        // Body is a UI2FieldControl, which slots Cancel beside the input.
        bodyIsFieldControl={true}
      >
        <UI2FieldControl>
          <label className='ui2-checkboxField-horizontalControl'>
            <input
              type='checkbox'
              className='ui2-checkboxField-input'
              checked={!!value}
              disabled={!!isDisabled}
              onChange={this.handleChange}
              data-testid={testID}
              aria-label={accessibilityLabel || label}
            />
            {this.renderBox(!!value, !!isDisabled)}
          </label>
        </UI2FieldControl>
      </UI2FieldShell>
    )
  }

  private renderTwoColumn() {
    const {
      label, tooltip: tooltipText, inlineHelpText, translationTable, frames,
      className, style, isDisabled, isReadOnly, value, accessibilityLabel, testID,
      enablePrefilledMode, isRequired, isOptional, prefilledEditText,
    } = this.props

    // The shell manages prefilled state; editNode is forwarded into renderReadOnly
    // so the Edit button aligns beside the value text rather than outside the
    // label+value block. Label is excluded from the shell's own rendering: in
    // editable mode it lives inline in the row; in read-only mode it lives inside
    // renderReadOnly above the value.
    const renderReadOnly = (val: boolean | undefined, { editNode }: IFieldShellReadOnlyHelpers) => (
      <div className='ui2-checkboxField--readOnlyStack'>
        <UI2Label
          labelText={label}
          tooltipText={tooltipText}
          isRequired={isRequired}
          isOptional={isOptional}
          inlineHelpText={inlineHelpText}
          translationTable={translationTable}
          frames={frames}
        />
        <div className='ui2-checkboxField-readOnlyValue'>
          <span className='ui2-checkboxField-readOnlyText'>{val ? 'Yes' : 'No'}</span>
          {editNode}
        </div>
      </div>
    )

    const renderEditable = (_children: React.ReactNode, { cancelNode }: IFieldShellEditableExtras) => (
      <div className='ui2-checkboxField-rowWrapper'>
        <label className='ui2-checkboxField-row'>
          <div className='ui2-checkboxField-checkboxColumn'>
            <input
              type='checkbox'
              className='ui2-checkboxField-input'
              checked={!!value}
              disabled={!!isDisabled}
              onChange={this.handleChange}
              data-testid={testID}
              aria-label={accessibilityLabel || label}
            />
            {this.renderBox(!!value, !!isDisabled)}
          </div>
          <div className='ui2-checkboxField-labelColumn'>
            <UI2Label
              labelText={label}
              tooltipText={tooltipText}
              isRequired={isRequired}
              isOptional={isOptional}
              inlineHelpText={inlineHelpText}
              translationTable={translationTable}
              frames={frames}
            />
          </div>
        </label>
        {cancelNode}
      </div>
    )

    return (
      <UI2FieldShell
        {...(this.props as unknown as IUI2FieldShellProps)}
        className={classNames('ui2-checkboxField', 'ui2-checkboxField--twoColumn', className)}
        style={style}
        // Suppress the shell's own label — we place it inside each branch
        // of renderReadOnly/renderEditable so it sits at the right position
        // relative to the value or is inline with the checkbox control.
        label={undefined}
        isHorizontalLayout={false}
        renderReadOnly={renderReadOnly}
        renderEditable={renderEditable}
        suppressDefaultEmptyState={true}
      >
        {null}
      </UI2FieldShell>
    )
  }

  private renderBox(checked: boolean, isDisabled: boolean) {
    return (
      <span
        className={classNames('ui2-checkboxField-box', {
          'ui2-checkboxField-box--checked': checked,
          'ui2-checkboxField-box--disabled': isDisabled,
        })}
        aria-hidden='true'
      >
        {checked && (
          <svg className='ui2-checkboxField-checkmark' viewBox='0 0 16 16'>
            <polyline
              points='3.5,8.5 6.5,11.5 12.5,5.5'
              fill='none'
              stroke='currentColor'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            />
          </svg>
        )}
      </span>
    )
  }

  private handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    this.props.onChange?.(event.target.checked)
  }
}
